import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { KoobButton } from '@koob/ui';
import { useApp, useError, useSnack } from 'hooks';
import { useAsync } from 'react-async';
import { getRequestAccess, sendRequestAccess } from 'api/toRequestAccess';
import { KoobStateEnum } from 'utils';
import background from 'images/trip-designer1.png';
import KoobLayout from '../../../components/Koob/KoobLayout';

export const Discover = () => {
  const { t } = useTranslation('tripDesigner', { keyPrefix: 'discover' });
  const { currentOrganizationId, user } = useApp();
  const { sendErrorSnack } = useError();
  const { notify } = useSnack();
  const [requesting, setRequesting] = useState(false);

  const { data, reload } = useAsync({
    promiseFn: getRequestAccess,
  });

  const requestAccess = data ? data?.data : {};
  // KD-3723: a validated organization has nothing to request here.
  if (requestAccess?.tripDesignerRequestState === KoobStateEnum.VALIDATED) {
    return <Navigate to="/trip-designer/trips" replace />;
  }
  const isRequested = requestAccess?.tripDesignerRequestState
    ? requestAccess?.tripDesignerRequestState !== KoobStateEnum.NOT_REQUESTED
    : false;

  const handleRequest = async () => {
    setRequesting(true);
    try {
      await sendRequestAccess(currentOrganizationId);
      notify(t('success'));
      reload();
    } catch (err) {
      sendErrorSnack(err);
    } finally {
      setRequesting(false);
    }
  };

  return (
    <KoobLayout>
      {/* ponytail: 8rem ≈ KoobLayout top bar + main paddings; exact value not critical */}
      <div className="relative min-h-[calc(100vh-8rem)] flex items-center justify-center p-6">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{ backgroundImage: `url(${background})` }}
        />
        <div className="relative w-full max-w-xl rounded-2xl border border-solid border-border bg-card/90 shadow-lg backdrop-blur px-8 py-10 flex flex-col items-center text-center">
          <div className="h-14 w-14 rounded-full bg-brand/10 text-brand flex items-center justify-center mb-4">
            <i className="fa-regular fa-location-dot text-2xl" />
          </div>
          <h1 className="m-0 text-2xl font-bold text-foreground">
            {t('title')}
          </h1>
          <ul className="mt-6 mb-0 w-full list-none p-0 space-y-3 text-left">
            {[1, 2, 3, 4, 5, 6].map(itemNumber => (
              <li
                key={itemNumber}
                className="flex items-start gap-3 text-sm text-muted-foreground"
              >
                <i className="fa-solid fa-check text-brand mt-0.5" />
                {t(`textItem${itemNumber}`)}
              </li>
            ))}
          </ul>
          {user?.role === 'to_admin' && (
            <div className="mt-8">
              {isRequested ? (
                <div className="inline-flex items-center gap-2 rounded-full bg-brand/10 text-brand px-4 py-2 text-sm font-medium">
                  <i className="fa-regular fa-clock" />
                  {t('requestPending')}
                </div>
              ) : (
                <KoobButton
                  variant="primary"
                  size="lg"
                  loading={requesting}
                  onClick={handleRequest}
                >
                  {t('requestAccess')}
                </KoobButton>
              )}
            </div>
          )}
        </div>
      </div>
    </KoobLayout>
  );
};
